<template>
  <div class="driver-machine">
    <div class="driver-machine__header d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-8">
      <div>
        <h3 class="font-size-h1 mb-2">{{ $t("DRIVER_ACCESS.MACHINE_TITLE") }}</h3>
        <div v-if="loading">{{ $t("DRIVER_ACCESS.LOADING") }}</div>
        <div v-else class="driver-machine__details d-flex flex-wrap flex-md-nowrap align-items-center">
          <span class="font-size-h4 font-weight-bold mr-3">{{ machine.registrationPlate || "-" }}</span>
          <span class="mr-3">{{ machine.machineName || "-" }}</span>
          <span class="mr-3">{{ machine.machineModel || "-" }}</span>
          <span>{{ machineTypeName }}</span>
        </div>
      </div>
      <button type="button" class="driver-machine__header-action btn btn-light font-weight-bold mt-4 mt-md-0" @click="logout">
        {{ $t("DRIVER_ACCESS.LOGOUT") }}
      </button>
    </div>

    <div v-if="dashboardError" role="alert" class="alert alert-danger">
      <div class="alert-text">{{ $t("DRIVER_ACCESS.DASHBOARD_ERROR") }}</div>
    </div>

    <div v-if="errors.length" role="alert" class="alert alert-danger">
      <div v-for="(error, index) in errors" :key="index" class="alert-text">{{ error }}</div>
    </div>

    <div v-if="success" role="alert" class="alert alert-success">
      <div class="alert-text">{{ $t("DRIVER_ACCESS.SIGNAL_SUCCESS") }}</div>
    </div>

    <section>
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-5">
        <h4 class="mb-0">{{ $t("DRIVER_ACCESS.CURRENT_WORK") }}</h4>
        <button type="button" class="driver-machine__report-signal btn btn-dark font-weight-bold mt-3 mb-2 mt-md-0 mb-md-0 ml-md-3" @click="isSignalFormOpen = !isSignalFormOpen">
          {{ $t("DRIVER_ACCESS.REPORT_SIGNAL") }}
        </button>
      </div>

      <b-form v-if="isSignalFormOpen" class="border rounded p-5 mb-6" @submit.stop.prevent="submitSignal">
        <h5>{{ $t("DRIVER_ACCESS.REPORT_SIGNAL") }}</h5>
        <b-form-group :label="$t('DRIVER_ACCESS.DATE_TIME')" label-for="driver-signal-date-time">
          <b-form-input id="driver-signal-date-time" v-model="dateTime" type="datetime-local" class="form-control form-control-solid" />
        </b-form-group>
        <b-form-group :label="$t('DRIVER_ACCESS.DESCRIPTION')" label-for="driver-signal">
          <b-form-textarea id="driver-signal" v-model.trim="description" rows="4" max-rows="10" :placeholder="$t('DRIVER_ACCESS.DESCRIPTION_PLACEHOLDER')" />
        </b-form-group>
        <div class="d-flex justify-content-end">
          <button ref="submit" type="submit" class="driver-machine__submit-signal btn btn-dark font-weight-bold px-9 py-4" :disabled="submitting">
            {{ $t("DRIVER_ACCESS.SEND_SIGNAL") }}
          </button>
        </div>
      </b-form>

      <div v-if="dashboardLoading">{{ $t("DRIVER_ACCESS.LOADING") }}</div>
      <template v-else>
        <article v-for="visit in currentVisits" :key="`visit-${visit.id}`" class="border rounded p-5 mb-4">
          <div class="driver-visit__header d-flex flex-wrap flex-md-nowrap align-items-center mb-3">
            <h5 class="driver-visit__title mb-0 mr-3">
              {{ visit.location || (visit.isOnRoad ? $t("DRIVER_ACCESS.ON_ROAD") : $t("DRIVER_ACCESS.SERVICE_VISIT")) }}
            </h5>
            <span class="driver-visit__date mr-3">{{ dateRange(visit.dateFrom, visit.dateTo) }}</span>
            <DriverStatusIndicator :status="visit.status" class="ml-md-auto" />
          </div>
          <p v-if="visit.workDone" class="mb-3 white-space-pre-wrap">{{ visit.workDone }}</p>
          <div v-if="visit.items && visit.items.length" class="driver-work-items">
            <div v-for="item in visit.items" :key="`current-work-item-${item.id}`" class="driver-work-item py-2 border-top">
              <div class="driver-work-item__content d-flex flex-wrap flex-md-nowrap align-items-center">
                <span class="driver-work-item__name font-weight-bold flex-grow-1 mr-3">
                  {{ itemDisplayName(item) }}
                </span>
                <DriverStatusIndicator :status="item.status" />
              </div>
              <div v-if="item.note" class="driver-work-item__note white-space-pre-wrap mt-2">{{ item.note }}</div>
            </div>
          </div>
          <div v-else>{{ $t("DRIVER_ACCESS.NO_TASKS") }}</div>
        </article>
        <p v-if="!currentVisits.length">{{ $t("DRIVER_ACCESS.NO_CURRENT_WORK") }}</p>

        <div v-if="pendingSignals.length" class="mt-8">
          <h5>{{ $t("DRIVER_ACCESS.AWAITING_ASSIGNMENT") }}</h5>
          <div v-for="signal in pendingSignals" :key="`pending-signal-${signal.id}`" class="border-top py-3">
            <div class="font-weight-bold">{{ signal.description }}</div>
            <div>{{ formatDateTime(signal.dateTime) }}</div>
          </div>
        </div>
      </template>
    </section>

    <section class="mt-10">
      <h4 class="mb-5">{{ $t("DRIVER_ACCESS.SERVICE_HISTORY") }}</h4>
      <ResponsiveDataTable
        class="driver-history-table driver-history-table--service elevation-1"
        :headers="serviceHistoryHeaders"
        :items="serviceHistory.items"
        :options.sync="serviceHistoryOptions"
        :items-per-page="itemsPerPage"
        :server-items-length="serviceHistory.totalItemsCount"
        :loading="serviceHistoryLoading"
        :loading-text="$t('DRIVER_ACCESS.LOADING')"
        :no-data-text="$t('DRIVER_ACCESS.NO_SERVICE_HISTORY')"
        :footer-props="tableFooterProps"
        :mobile-summary-fields="serviceHistoryMobileSummaryFields"
        :mobile-detail-fields="serviceHistoryMobileDetailFields"
        :mobile-toggle-label="$t('DRIVER_ACCESS.DETAILS')"
        @update:options="onServiceHistoryOptionsChanged"
      >
        <template v-slot:[`item.serviceType`]="{ item }">
          {{ item.serviceType || $t("DRIVER_ACCESS.SERVICE_RECORD") }}
        </template>
        <template v-slot:[`item.description`]="{ item }">
          <span class="white-space-pre-wrap">{{ item.description || "-" }}</span>
        </template>
        <template v-slot:[`item.mileage`]="{ item }">
          {{ serviceMileageText(item) }}
        </template>
      </ResponsiveDataTable>
    </section>

    <section class="mt-10">
      <h4 class="mb-5">{{ $t("DRIVER_ACCESS.SIGNALS_AND_TASKS") }}</h4>
      <ResponsiveDataTable
        class="driver-history-table driver-history-table--work elevation-1"
        :headers="workHistoryHeaders"
        :items="workHistory.items"
        :options.sync="workHistoryOptions"
        :items-per-page="itemsPerPage"
        :server-items-length="workHistory.totalItemsCount"
        :loading="workHistoryLoading"
        :loading-text="$t('DRIVER_ACCESS.LOADING')"
        :no-data-text="$t('DRIVER_ACCESS.NO_WORK_HISTORY')"
        :footer-props="tableFooterProps"
        :mobile-summary-fields="workHistoryMobileSummaryFields"
        :mobile-detail-fields="workHistoryMobileDetailFields"
        :mobile-toggle-label="$t('DRIVER_ACCESS.DETAILS')"
        @update:options="onWorkHistoryOptionsChanged"
      >
        <template v-slot:[`item.note`]="{ item }">
          <span class="white-space-pre-wrap">{{ item.note || "-" }}</span>
        </template>
        <template v-slot:[`item.workDone`]="{ item }">
          <span class="white-space-pre-wrap">{{ workDoneText(item) }}</span>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <DriverStatusIndicator :status="item.status" />
        </template>
        <template v-slot:[`item.date`]="{ item }">
          {{ workItemDate(item) }}
        </template>
        <template v-slot:mobile-summary-status="{ item }">
          <DriverStatusIndicator :status="item.status" />
        </template>
      </ResponsiveDataTable>
    </section>
  </div>
</template>

<script>
import DriverAccessService from "@/modules/driver-access/services/driver-access.service";
import DriverStatusIndicator from "@/modules/driver-access/components/DriverStatusIndicator.vue";
import ResponsiveDataTable from "@/core/components/table/ResponsiveDataTable.vue";

const emptyPage = () => ({ items: [], totalItemsCount: 0, page: 1 });

export default {
  name: "driver-machine",
  components: {
    DriverStatusIndicator,
    ResponsiveDataTable
  },
  data() {
    return {
      machine: {},
      currentVisits: [],
      pendingSignals: [],
      serviceHistory: emptyPage(),
      workHistory: emptyPage(),
      serviceHistoryPage: 1,
      workHistoryPage: 1,
      dashboardRequestId: 0,
      serviceHistoryOptions: { page: 1, itemsPerPage: 20 },
      workHistoryOptions: { page: 1, itemsPerPage: 20 },
      dateTime: this.currentDateTime(),
      description: "",
      errors: [],
      success: false,
      loading: true,
      dashboardLoading: true,
      dashboardError: false,
      serviceHistoryLoading: false,
      workHistoryLoading: false,
      submitting: false,
      isSignalFormOpen: false,
      itemsPerPage: 20,
      tableFooterProps: {
        showFirstLastPage: true,
        itemsPerPageOptions: [20],
        firstIcon: "mdi-arrow-collapse-left",
        lastIcon: "mdi-arrow-collapse-right"
      },
      serviceHistoryHeaders: [
        { text: this.$i18n.t("FORM_LABELS.SERVICE_TYPE"), value: "serviceType", sortable: false },
        { text: this.$i18n.t("FORM_LABELS.DESCRIPTION"), value: "description", sortable: false },
        { text: this.$i18n.t("SERVICE_VISITS.HISTORY.FIELDS.DATE_TO"), value: "date", sortable: false },
        { text: this.$i18n.t("SERVICE_VISITS.HISTORY.FIELDS.SERVICE_MILEAGE"), value: "mileage", sortable: false }
      ],
      workHistoryHeaders: [
        { text: this.$i18n.t("SERVICE_VISITS.TASK_NAME"), value: "name", sortable: false },
        { text: this.$i18n.t("SERVICE_VISITS.HISTORY.FIELDS.NOTE"), value: "note", sortable: false },
        { text: this.$i18n.t("SERVICE_VISITS.HISTORY.FIELDS.WORK_DONE"), value: "workDone", sortable: false },
        { text: this.$i18n.t("FORM_LABELS.STATUS"), value: "status", sortable: false },
        { text: this.$i18n.t("SERVICE_VISITS.HISTORY.FIELDS.DATE_TO"), value: "date", sortable: false }
      ]
    };
  },
  computed: {
    machineTypeName() {
      return this.machine.machineType ? this.machine.machineType.name : "-";
    },
    serviceHistoryMobileSummaryFields() {
      return [
        {
          key: "serviceType",
          label: this.$t("FORM_LABELS.SERVICE_TYPE"),
          primary: true,
          value: item => item.serviceType || this.$t("DRIVER_ACCESS.SERVICE_RECORD")
        },
        {
          key: "date",
          label: this.$t("SERVICE_VISITS.HISTORY.FIELDS.DATE_TO")
        },
        {
          key: "mileage",
          label: this.$t("SERVICE_VISITS.HISTORY.FIELDS.SERVICE_MILEAGE"),
          value: item => this.serviceMileageText(item)
        }
      ];
    },
    serviceHistoryMobileDetailFields() {
      return [
        {
          key: "description",
          label: this.$t("FORM_LABELS.DESCRIPTION")
        }
      ];
    },
    workHistoryMobileSummaryFields() {
      return [
        {
          key: "name",
          label: this.$t("SERVICE_VISITS.TASK_NAME"),
          primary: true
        },
        {
          key: "status",
          label: this.$t("FORM_LABELS.STATUS"),
          slot: "status"
        },
        {
          key: "date",
          label: this.$t("SERVICE_VISITS.HISTORY.FIELDS.DATE_TO"),
          value: item => this.workItemDate(item)
        }
      ];
    },
    workHistoryMobileDetailFields() {
      return [
        {
          key: "note",
          label: this.$t("SERVICE_VISITS.HISTORY.FIELDS.NOTE")
        },
        {
          key: "workDone",
          label: this.$t("SERVICE_VISITS.HISTORY.FIELDS.WORK_DONE"),
          value: item => this.workDoneText(item)
        }
      ];
    }
  },
  mounted() {
    if (!DriverAccessService.hasToken()) {
      this.$router.push({ name: "driver-login" });
      return;
    }

    this.loadDashboard();
  },
  methods: {
    currentDateTime() {
      const date = new Date();
      const offsetMs = date.getTimezoneOffset() * 60000;

      return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
    },
    loadDashboard(serviceMachinesPage = this.serviceHistoryPage, workItemsPage = this.workHistoryPage) {
      const requestId = ++this.dashboardRequestId;

      this.dashboardError = false;
      this.dashboardLoading = true;
      this.serviceHistoryLoading = true;
      this.workHistoryLoading = true;
      DriverAccessService.serviceDashboard({ serviceMachinesPage, workItemsPage })
        .then(({ data }) => {
          if (requestId !== this.dashboardRequestId) {
            return;
          }

          this.machine = data.data.machine || {};
          this.currentVisits = data.data.currentVisits || [];
          this.pendingSignals = data.data.pendingSignals || [];
          this.serviceHistory = data.data.serviceMachines || emptyPage();
          this.workHistory = data.data.workItems || emptyPage();
          this.serviceHistoryPage = serviceMachinesPage;
          this.workHistoryPage = workItemsPage;
        })
        .catch(error => {
          if (requestId === this.dashboardRequestId) {
            this.handleDashboardError(error);
          }
        })
        .finally(() => {
          if (requestId !== this.dashboardRequestId) {
            return;
          }

          this.loading = false;
          this.dashboardLoading = false;
          this.serviceHistoryLoading = false;
          this.workHistoryLoading = false;
        });
    },
    submitSignal() {
      this.errors = [];
      this.success = false;
      if (!this.description || this.description.length < 3) {
        this.errors = [this.$t("DRIVER_ACCESS.DESCRIPTION_REQUIRED")];
        return;
      }
      if (!this.dateTime) {
        this.errors = [this.$t("DRIVER_ACCESS.DATE_TIME_REQUIRED")];
        return;
      }

      const submitButton = this.$refs.submit;
      this.submitting = true;
      if (submitButton) submitButton.classList.add("spinner", "spinner-light", "spinner-right");

      DriverAccessService.createSignal({ dateTime: this.dateTime, description: this.description })
        .then(() => {
          this.dateTime = this.currentDateTime();
          this.description = "";
          this.success = true;
          this.isSignalFormOpen = false;
          this.loadDashboard();
        })
        .catch(({ response }) => {
          this.errors = this.extractErrors(response);
        })
        .finally(() => {
          this.submitting = false;
          if (submitButton) submitButton.classList.remove("spinner", "spinner-light", "spinner-right");
        });
    },
    itemDisplayName(item) {
      const name = item && item.name ? item.name : "-";
      const prefixes = {
        service_type: this.$t("DRIVER_ACCESS.SERVICE_TYPE"),
        machine_service_signal: this.$t("DRIVER_ACCESS.SIGNAL")
      };
      const prefix = prefixes[item && item.type];

      return prefix ? `${prefix}: ${name}` : name;
    },
    dateRange(from, to) {
      return from === to || !to ? from || "-" : `${from || "-"} – ${to}`;
    },
    formatDateTime(value) {
      return value ? new Date(value.replace(" ", "T")).toLocaleString() : "-";
    },
    workItemDate(item) {
      return item.signalDateTime ? this.formatDateTime(item.signalDateTime) : this.dateRange(item.visit && item.visit.dateFrom, item.visit && item.visit.dateTo);
    },
    serviceMileageText(item) {
      if (item.mileage === null || item.mileage === undefined) {
        return "-";
      }

      return `${item.mileage} ${item.isMileage ? this.$t("DRIVER_ACCESS.KILOMETERS") : this.$t("DRIVER_ACCESS.MOTO_HOURS")}`;
    },
    workDoneText(item) {
      return item.visit && item.visit.workDone ? item.visit.workDone : "-";
    },
    onServiceHistoryOptionsChanged(options) {
      if (options.page !== this.serviceHistoryPage) {
        this.serviceHistoryPage = options.page;
        this.loadDashboard(options.page, this.workHistoryPage);
      }
    },
    onWorkHistoryOptionsChanged(options) {
      if (options.page !== this.workHistoryPage) {
        this.workHistoryPage = options.page;
        this.loadDashboard(this.serviceHistoryPage, options.page);
      }
    },
    logout() {
      DriverAccessService.logout().finally(() => {
        this.$router.push({ name: "driver-login" });
      });
    },
    handleUnauthorized() {
      DriverAccessService.clearTokens();
      this.$router.push({ name: "driver-login" });
    },
    handleDashboardError(error) {
      if (error.response && error.response.status === 401) {
        this.handleUnauthorized();
        return;
      }

      this.dashboardError = true;
    },
    extractErrors(response) {
      if (!response || !response.data) return [this.$t("DRIVER_ACCESS.SIGNAL_ERROR")];
      if (Array.isArray(response.data.message)) return response.data.message;
      if (response.data.message) return [response.data.message];
      return [this.$t("DRIVER_ACCESS.SIGNAL_ERROR")];
    }
  }
};
</script>

<style scoped>
.driver-machine { width: 100%; max-width: 960px; }
.white-space-pre-wrap { white-space: pre-wrap; }

.driver-machine__details > span {
  margin-bottom: 0.25rem;
}

.driver-visit__title,
.driver-visit__date {
  margin-bottom: 0.25rem;
}

.driver-visit__date {
  color: #7e8299;
  white-space: nowrap;
}

.driver-work-item__name {
  margin-bottom: 0.25rem;
}

.driver-work-item__name {
  min-width: 12rem;
}

.driver-history-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.driver-history-table--service ::v-deep table {
  min-width: 680px;
}

.driver-history-table--work ::v-deep table {
  min-width: 860px;
}

@media (max-width: 767.98px) {
  .driver-machine__header-action,
  .driver-machine__report-signal,
  .driver-machine__submit-signal {
    width: 100%;
  }

}
</style>
